import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Select as MuiSelect,
  MenuItem,
  FormControl,
  Tabs,
  Tab,
  Box,
  Typography,
  Chip,
  Checkbox,
  ListItemText,
  OutlinedInput,
} from "@mui/material";
import {
  Iconography,
  IconographySizes,
  IconographyThemes,
  Button,
  Toggle,
  ToggleSizes,
  Loader,
  LoaderSizes,
} from "@hid-galaxy-ui/galaxy-react";
import { useAppDispatch, useAppSelector } from "../../../../../../redux";
import { getTechnologiesSagaAction } from "../../../../../../redux/saga/technology-settings/technologies/saga-actions";
import {
  getCategoriesSagaAction,
  getTestCasesSagaAction,
} from "../../../../../../redux/saga/project-validation/saga-actions";
import "../Modals.scss";

interface AddRequirementModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: any) => void;
  editData?: any;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
};

const AddRequirementModal: React.FC<AddRequirementModalProps> = ({
  open,
  onClose,
  onSave,
  editData,
}) => {
  const dispatch = useAppDispatch();
  const { technologies, status: technologySettingsStatus } = useAppSelector(
    (state) => state.technology_settings_technologies
  );
  const { categories, testCases, status } = useAppSelector(
    (state) => state.project_validation
  );

  const isSubmitting = editData
    ? status.updateRequirementLoading
    : status.createRequirementLoading;

  const [activeTab, setActiveTab] = useState(0);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [formData, setFormData] = useState({
    category: "",
    requirementType: "",
    technology: [] as string[],
    integrationType: [] as string[],
    testCases: [] as string[],
    validationScore: "",
    status: true,
    highLevelScenario: "",
    requirementDescription: "",
  });

  const activeTechnologies = React.useMemo(
    () => technologies.filter((technology) => technology.status === "Active"),
    [technologies]
  );

  const technologyOptions = React.useMemo(
    () =>
      activeTechnologies.map((technology) => ({
        label: technology.name,
        value: String(technology.id),
      })),
    [activeTechnologies]
  );

  const allActiveIntegrations = React.useMemo(() => {
    const byId = new Map<string, { label: string; value: string }>();
    activeTechnologies.forEach((technology) => {
      technology.integrations
        .filter((integration) => integration.status === "Active")
        .forEach((integration) => {
          const value = String(integration.id);
          if (!byId.has(value)) {
            byId.set(value, {
              label: integration.name,
              value,
            });
          }
        });
    });
    return Array.from(byId.values()).sort((a, b) =>
      a.label.localeCompare(b.label)
    );
  }, [activeTechnologies]);

  const integrationOptions = React.useMemo(() => {
    if (formData.technology.length === 0) {
      return allActiveIntegrations;
    }

    const byId = new Map<string, { label: string; value: string }>();
    activeTechnologies
      .filter((technology) =>
        formData.technology.includes(String(technology.id))
      )
      .forEach((technology) => {
        technology.integrations
          .filter((integration) => integration.status === "Active")
          .forEach((integration) => {
            const value = String(integration.id);
            if (!byId.has(value)) {
              byId.set(value, {
                label: integration.name,
                value,
              });
            }
          });
      });

    // Keep any already-selected integrations that may be inactive/unscoped so edit UI stays consistent
    formData.integrationType.forEach((selectedId) => {
      if (!byId.has(selectedId)) {
        const fromAll = allActiveIntegrations.find(
          (option) => option.value === selectedId
        );
        if (fromAll) {
          byId.set(selectedId, fromAll);
        } else {
          byId.set(selectedId, { label: selectedId, value: selectedId });
        }
      }
    });

    return Array.from(byId.values()).sort((a, b) =>
      a.label.localeCompare(b.label)
    );
  }, [
    activeTechnologies,
    allActiveIntegrations,
    formData.technology,
    formData.integrationType,
  ]);

  const getIntegrationLabel = React.useCallback(
    (integrationId: string) => {
      const option = integrationOptions.find(
        (integrationOption) => integrationOption.value === integrationId
      );
      if (option) return option.label;
      const fromAll = allActiveIntegrations.find(
        (integrationOption) => integrationOption.value === integrationId
      );
      return fromAll?.label || integrationId;
    },
    [allActiveIntegrations, integrationOptions]
  );

  const mapSavedTechnologiesToFormValues = React.useCallback(
    (values: unknown[]): string[] => {
      return values
        .map((value) => {
          if (typeof value === "object" && value !== null && "id" in value) {
            const id = String((value as { id: unknown }).id).trim();
            if (id) {
              const byId = activeTechnologies.find(
                (technology) => String(technology.id) === id
              );
              if (byId) {
                return String(byId.id);
              }
            }
          }

          const nameFromRef =
            typeof value === "object" &&
            value !== null &&
            "name" in value &&
            (value as { name: unknown }).name != null
              ? String((value as { name: unknown }).name).trim()
              : "";
          const normalized = (nameFromRef || String(value).trim()).trim();
          if (!normalized || normalized === "[object Object]") {
            return null;
          }

          const byId = activeTechnologies.find(
            (technology) => String(technology.id) === normalized
          );
          if (byId) {
            return String(byId.id);
          }

          const legacyName =
            normalized === "Identity Positioning" ? "OIP" : normalized;
          const byName = activeTechnologies.find(
            (technology) => technology.name === legacyName
          );
          return byName ? String(byName.id) : null;
        })
        .filter((value): value is string => Boolean(value));
    },
    [activeTechnologies]
  );

  const mapSavedIntegrationsToFormValues = React.useCallback(
    (values: unknown[]): string[] => {
      const integrationsById = new Map<string, string>();
      const integrationsByName = new Map<string, string>();

      activeTechnologies.forEach((technology) => {
        technology.integrations.forEach((integration) => {
          const id = String(integration.id);
          integrationsById.set(id, id);
          integrationsByName.set(integration.name, id);
        });
      });

      return values
        .map((value) => {
          if (typeof value === "object" && value !== null) {
            const ref = value as { id?: unknown; name?: unknown };
            if (ref.id != null && String(ref.id).trim()) {
              const id = String(ref.id).trim();
              return integrationsById.get(id) || id;
            }
            if (ref.name != null && String(ref.name).trim()) {
              const name = String(ref.name).trim();
              return integrationsByName.get(name) || null;
            }
            return null;
          }

          const normalized = String(value ?? "").trim();
          if (!normalized || normalized === "[object Object]") {
            return null;
          }
          if (integrationsById.has(normalized)) {
            return normalized;
          }
          return integrationsByName.get(normalized) || null;
        })
        .filter((value): value is string => Boolean(value));
    },
    [activeTechnologies]
  );

  // Fetch technologies, categories, and test cases when modal opens
  React.useEffect(() => {
    if (open) {
      dispatch(getTechnologiesSagaAction());
      if (categories.length === 0) {
        dispatch(getCategoriesSagaAction());
      }
      if (testCases.length === 0) {
        dispatch(getTestCasesSagaAction());
      }
    }
  }, [dispatch, open, categories.length, testCases.length]);

  React.useEffect(() => {
    if (!open) {
      return;
    }

    if (editData) {
      const rawTechValues = Array.isArray(editData.technology)
        ? editData.technology
        : editData.technology
          ? String(editData.technology).split(",")
          : [];
      const techArray = mapSavedTechnologiesToFormValues(rawTechValues);
      const intArray = Array.isArray(editData.integration_type)
        ? mapSavedIntegrationsToFormValues(editData.integration_type)
        : editData.integration_type
          ? String(editData.integration_type)
              .split(",")
              .map((value: string) => value.trim())
              .filter(Boolean)
          : [];
      const tcArray = Array.isArray(editData.test_cases)
        ? editData.test_cases
        : editData.test_cases
          ? editData.test_cases.split(",").map((tc: string) => tc.trim())
          : [];

      setFormData({
        category: String(editData.category_id || ""),
        requirementType: editData.requirement_type || "",
        technology: techArray,
        integrationType: intArray,
        testCases: tcArray,
        validationScore: editData.validation_score || "",
        status:
          editData.requirement_status === "Active" ||
          editData.requirement_status === "active",
        highLevelScenario: editData.high_level_scenario || "",
        requirementDescription: editData.requirement_description || "",
      });
    } else {
      setFormData({
        category: "",
        requirementType: "",
        technology: [],
        integrationType: [],
        testCases: [],
        validationScore: "",
        status: true,
        highLevelScenario: "",
        requirementDescription: "",
      });
      setActiveTab(0);
    }
    setSubmitted(false);
    setErrors({});
  }, [editData, open, mapSavedTechnologiesToFormValues, mapSavedIntegrationsToFormValues]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | { name?: string; value: unknown }>
  ) => {
    const name = e.target.name as string;
    let value = e.target.value;

    // Allow only numbers for validationScore field
    if (name === "validationScore") {
      value = (value as string).replace(/[^0-9]/g, "");
    }

    if (name === "technology") {
      const nextTechnologyIds = (value as string[]) || [];
      const allowedIntegrationIds = new Set<string>();
      activeTechnologies
        .filter((technology) =>
          nextTechnologyIds.includes(String(technology.id))
        )
        .forEach((technology) => {
          technology.integrations.forEach((integration) => {
            allowedIntegrationIds.add(String(integration.id));
          });
        });

      setFormData((prev) => ({
        ...prev,
        technology: nextTechnologyIds,
        integrationType:
          nextTechnologyIds.length === 0
            ? prev.integrationType
            : prev.integrationType.filter((id) =>
                allowedIntegrationIds.has(id)
              ),
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }

    if (name === "category") {
      setFormData((prev) => ({
        ...prev,
        testCases: [],
      }));
    }

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: "",
      });
    }
  };

  const handleSwitchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      status: e.target.checked,
    });
  };

  const handleNext = () => {
    setActiveTab(1);
  };

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.category) {
      newErrors.category = "Category is required";
    }
    if (!formData.requirementType) {
      newErrors.requirementType = "Requirement type is required";
    }
    if (!formData.technology || formData.technology.length === 0) {
      newErrors.technology = "Technology is required";
    }
    if (!formData.integrationType || formData.integrationType.length === 0) {
      newErrors.integrationType = "Integration type is required";
    }
    if (!formData.testCases || formData.testCases.length === 0) {
      newErrors.testCases = "Test cases is required";
    }
    if (!formData.validationScore) {
      newErrors.validationScore = "Validation score is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validateForm() && !isSubmitting) {
      setSubmitted(true);
      onSave({
        ...formData,
        technology: formData.technology.map((technologyId) =>
          Number(technologyId)
        ),
        integrationType: formData.integrationType.map((integrationId) =>
          Number(integrationId)
        ),
      });
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setSubmitted(false);
      setErrors({});
      setFormData({
        category: "",
        requirementType: "",
        technology: [],
        integrationType: [],
        testCases: [],
        validationScore: "",
        status: true,
        highLevelScenario: "",
        requirementDescription: "",
      });
      setActiveTab(0);
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      className="requirement-modal"
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: "0px 12px 24px rgba(0,0,0,0.12)",
          overflow: "hidden",
        },
      }}
    >
      {submitted ? (
        <div
          style={{
            padding: "40px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "16px",
          }}
        >
          {isSubmitting ? (
            <>
              <Loader variant={LoaderSizes.large} />
              <Typography
                variant="body2"
                sx={{ color: "#374151", textAlign: "center" }}
              >
                {editData
                  ? "Updating requirement..."
                  : "Creating requirement..."}
              </Typography>
            </>
          ) : (
            <>
              <Iconography
                icon="circleCheck"
                size={IconographySizes.XLarge}
                theme={IconographyThemes.Success}
              />
              <Typography
                variant="body2"
                sx={{ color: "#374151", textAlign: "center" }}
              >
                {editData
                  ? "Requirement updated successfully"
                  : "Requirement added successfully"}
              </Typography>
              <Button onClick={handleClose} variant="primary" label="Close" />
            </>
          )}
        </div>
      ) : (
        <>
          <DialogTitle>
            {editData ? "Update Requirement" : "Add Requirement"}
            {/* <Typography className="subtitle">

            </Typography> */}
            <IconButton
              aria-label="close"
              onClick={handleClose}
              disabled={isSubmitting}
              sx={{
                position: "absolute",
                right: 8,
                top: 8,
              }}
            >
              <Iconography icon="xmark" />
            </IconButton>
          </DialogTitle>

          <Box className="modal-tabs">
            <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)}>
              <Tab label="Requirement" />
              <Tab label="Description" />
            </Tabs>
          </Box>

          <DialogContent>
            <TabPanel value={activeTab} index={0}>
              <div className="form-field">
                <label>
                  Category <span className="required">*</span>
                </label>
                <FormControl
                  fullWidth
                  className="custom-select"
                  error={!!errors.category}
                >
                  <MuiSelect
                    name="category"
                    value={formData.category}
                    onChange={handleChange as any}
                    displayEmpty
                    renderValue={(selected) => {
                      if (!selected) {
                        return (
                          <span
                            style={{
                              color: "#9CA3AF",
                              fontFamily: "'Roboto', sans-serif",
                            }}
                          >
                            Select
                          </span>
                        );
                      }
                      return (
                        categories.find(
                          (category) => String(category.ID) === selected
                        )?.category_name || ""
                      );
                    }}
                  >
                    {categories.map((category) => (
                      <MenuItem key={category.ID} value={String(category.ID)}>
                        {category.category_name}
                      </MenuItem>
                    ))}
                  </MuiSelect>
                  {errors.category && (
                    <Typography
                      sx={{ color: "#d32f2f", fontSize: "0.75rem", mt: 0.5 }}
                    >
                      {errors.category}
                    </Typography>
                  )}
                </FormControl>
              </div>

              <div className="form-field">
                <label>
                  Requirement Type <span className="required">*</span>
                </label>
                <FormControl
                  fullWidth
                  className="custom-select"
                  error={!!errors.requirementType}
                >
                  <MuiSelect
                    name="requirementType"
                    value={formData.requirementType}
                    onChange={handleChange as any}
                    displayEmpty
                    renderValue={(selected) => {
                      if (!selected) {
                        return (
                          <span
                            style={{
                              color: "#9CA3AF",
                              fontFamily: "'Roboto', sans-serif",
                            }}
                          >
                            Select
                          </span>
                        );
                      }
                      return selected;
                    }}
                  >
                    <MenuItem value="Mandatory">Mandatory</MenuItem>
                    <MenuItem value="Best-Practice">Best-Practice</MenuItem>
                    <MenuItem value="Recommended">Recommended</MenuItem>
                    <MenuItem value="Optional">Optional</MenuItem>
                  </MuiSelect>
                  {errors.requirementType && (
                    <Typography
                      sx={{ color: "#d32f2f", fontSize: "0.75rem", mt: 0.5 }}
                    >
                      {errors.requirementType}
                    </Typography>
                  )}
                </FormControl>
              </div>

              <div className="form-field">
                <label>
                  Technology <span className="required">*</span>
                </label>
                <FormControl
                  fullWidth
                  className="custom-select"
                  error={!!errors.technology}
                >
                  <MuiSelect
                    name="technology"
                    multiple
                    value={formData.technology || []}
                    onChange={handleChange as any}
                    input={<OutlinedInput />}
                    displayEmpty
                    MenuProps={{ disablePortal: true }}
                    renderValue={(selected) => {
                      if (selected.length === 0) {
                        return (
                          <em
                            style={{
                              fontFamily: "'Roboto', sans-serif",
                              fontStyle: "normal",
                              color: "#9CA3AF",
                            }}
                          >
                            Select Technology
                          </em>
                        );
                      }
                      return (
                        <Box
                          sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}
                        >
                          {selected.map((value) => {
                            const option = technologyOptions.find(
                              (technologyOption) =>
                                technologyOption.value === value
                            );
                            return (
                              <Chip
                                key={value}
                                label={option?.label || value}
                              />
                            );
                          })}
                        </Box>
                      );
                    }}
                  >
                    {technologySettingsStatus.technologiesLoading &&
                    technologyOptions.length === 0 ? (
                      <MenuItem disabled value="">
                        Loading technologies...
                      </MenuItem>
                    ) : (
                      technologyOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          <Checkbox
                            checked={formData.technology.includes(option.value)}
                          />
                          <ListItemText primary={option.label} />
                        </MenuItem>
                      ))
                    )}
                  </MuiSelect>
                  {errors.technology && (
                    <Typography
                      sx={{ color: "#d32f2f", fontSize: "0.75rem", mt: 0.5 }}
                    >
                      {errors.technology}
                    </Typography>
                  )}
                </FormControl>
              </div>

              <div className="form-field">
                <label>
                  Integration type <span className="required">*</span>
                </label>
                <FormControl
                  fullWidth
                  className="custom-select"
                  error={!!errors.integrationType}
                >
                  <MuiSelect
                    name="integrationType"
                    multiple
                    value={formData.integrationType || []}
                    onChange={handleChange as any}
                    input={<OutlinedInput />}
                    displayEmpty
                    MenuProps={{ disablePortal: true }}
                    renderValue={(selected) => {
                      if (selected.length === 0) {
                        return (
                          <em
                            style={{
                              fontFamily: "'Roboto', sans-serif",
                              fontStyle: "normal",
                              color: "#9CA3AF",
                            }}
                          >
                            Select Integration Type
                          </em>
                        );
                      }
                      return (
                        <Box
                          sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}
                        >
                          {selected.map((value) => (
                            <Chip
                              key={value}
                              label={getIntegrationLabel(String(value))}
                            />
                          ))}
                        </Box>
                      );
                    }}
                  >
                    {integrationOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        <Checkbox
                          checked={formData.integrationType.includes(
                            option.value
                          )}
                        />
                        <ListItemText primary={option.label} />
                      </MenuItem>
                    ))}
                  </MuiSelect>
                  {errors.integrationType && (
                    <Typography
                      sx={{ color: "#d32f2f", fontSize: "0.75rem", mt: 0.5 }}
                    >
                      {errors.integrationType}
                    </Typography>
                  )}
                </FormControl>
              </div>

              <div className="form-field">
                <label>
                  Test cases <span className="required">*</span>
                </label>
                <FormControl
                  fullWidth
                  className="custom-select"
                  error={!!errors.testCases}
                >
                  <MuiSelect
                    name="testCases"
                    multiple
                    value={formData.testCases || []}
                    onChange={handleChange as any}
                    input={<OutlinedInput />}
                    displayEmpty
                    MenuProps={{ disablePortal: true }}
                    renderValue={(selected) => {
                      if (selected.length === 0) {
                        return (
                          <em
                            style={{
                              fontFamily: "'Roboto', sans-serif",
                              fontStyle: "normal",
                              color: "#9CA3AF",
                            }}
                          >
                            Select Test Cases
                          </em>
                        );
                      }
                      return (
                        <Box
                          sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}
                        >
                          {selected.map((value) => (
                            <Chip key={value} label={`TC-${value}`} />
                          ))}
                        </Box>
                      );
                    }}
                  >
                    {formData.category ? (
                      testCases
                        .filter(
                          (tc) => tc.category_id === Number(formData.category)
                        )
                        .map((testCase) => (
                          <MenuItem
                            key={testCase.ID}
                            value={String(testCase.ID)}
                          >
                            <Checkbox
                              checked={formData.testCases.includes(
                                String(testCase.ID)
                              )}
                            />
                            <ListItemText
                              primary={`TC-${testCase.ID} - ${
                                testCase.high_level_scenario || "N/A"
                              }`}
                            />
                          </MenuItem>
                        ))
                    ) : (
                      <MenuItem disabled>
                        Please select a category first
                      </MenuItem>
                    )}
                  </MuiSelect>
                  {errors.testCases && (
                    <Typography
                      sx={{ color: "#d32f2f", fontSize: "0.75rem", mt: 0.5 }}
                    >
                      {errors.testCases}
                    </Typography>
                  )}
                </FormControl>
              </div>

              <div className="form-field">
                <label>
                  Validation score <span className="required">*</span>
                </label>
                <TextField
                  fullWidth
                  className="validation-score-input"
                  name="validationScore"
                  value={formData.validationScore}
                  onChange={handleChange}
                  placeholder="Enter here"
                  variant="outlined"
                  error={!!errors.validationScore}
                  helperText={errors.validationScore}
                />
              </div>

              <div className="form-field">
                <label>Requirement Status</label>
                <div className="status-toggle-wrapper">
                  <Toggle
                    id="requirement-status"
                    labelText=""
                    labelON=""
                    labelOFF=""
                    onClick={(e) =>
                      handleSwitchChange({
                        target: { checked: !formData.status },
                      } as React.ChangeEvent<HTMLInputElement>)
                    }
                    toggled={formData.status}
                    size={ToggleSizes.Mini}
                  />
                  <span className="status-text">
                    {formData.status ? "Active" : "Inactive"}
                  </span>
                </div>
              </div>
            </TabPanel>

            <TabPanel value={activeTab} index={1}>
              <div className="form-field">
                <div className="field-label-row">
                  <label>High Level Scenario</label>
                  <div className="char-count">
                    {formData.highLevelScenario.length}/1000
                  </div>
                </div>
                <TextField
                  fullWidth
                  name="highLevelScenario"
                  value={formData.highLevelScenario}
                  onChange={handleChange}
                  multiline
                  rows={4}
                  placeholder="Enter High Level Scenario here"
                  variant="outlined"
                />
              </div>

              <div className="form-field">
                <div className="field-label-row">
                  <label>Requirement description</label>
                  <div className="char-count">
                    {formData.requirementDescription.length}/1000
                  </div>
                </div>
                <TextField
                  fullWidth
                  name="requirementDescription"
                  value={formData.requirementDescription}
                  onChange={handleChange}
                  multiline
                  rows={4}
                  placeholder="Enter Requirement Description here"
                  variant="outlined"
                />
              </div>
            </TabPanel>
          </DialogContent>

          <DialogActions>
            <Button
              onClick={handleClose}
              variant="secondary"
              label="Cancel"
              disabled={isSubmitting}
            />
            <Button
              onClick={activeTab === 0 ? handleNext : handleSubmit}
              variant="primary"
              label={activeTab === 0 ? "NEXT" : editData ? "UPDATE" : "ADD"}
              disabled={isSubmitting}
            />
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};

export default AddRequirementModal;
